import type { FormField, FieldType, FieldOption } from '../types.js';

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'number', label: 'Number' },
  { value: 'url', label: 'URL' },
  { value: 'file', label: 'File upload' },
];

interface Props {
  field: FormField;
  onChange: (updated: FormField) => void;
  onRemove: () => void;
}

export default function FieldEditor({ field, onChange, onRemove }: Props) {
  function update(patch: Partial<FormField>) {
    onChange({ ...field, ...patch });
  }

  const options = field.options ?? [];

  function updateOption(idx: number, patch: Partial<FieldOption>) {
    const next = options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    update({ options: next });
  }

  function addOption() {
    update({ options: [...options, { value: '', label: '' }] });
  }

  function removeOption(idx: number) {
    update({ options: options.filter((_, i) => i !== idx) });
  }

  const isFile = field.type === 'file';
  const needsOptions = field.type === 'select' || field.type === 'checkbox';

  return (
    <div className="card" style={{ marginBottom: '.75rem' }}>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: '2', minWidth: '140px' }}>
          <label>Field name (snake_case)</label>
          <input
            type="text"
            value={field.name}
            onChange={(e) => update({ name: e.target.value.replace(/\s+/g, '_').toLowerCase() })}
            placeholder="e.g. first_name"
          />
        </div>
        <div className="form-group" style={{ flex: '2', minWidth: '140px' }}>
          <label>Label</label>
          <input
            type="text"
            value={field.label}
            onChange={(e) => update({ label: e.target.value })}
            placeholder="Displayed to user"
          />
        </div>
        <div className="form-group" style={{ flex: '1', minWidth: '110px' }}>
          <label>Type</label>
          <select value={field.type} onChange={(e) => update({ type: e.target.value as FieldType })}>
            {FIELD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '.35rem', fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={field.required ?? false}
              onChange={(e) => update({ required: e.target.checked })}
            />
            Required
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {!isFile && (
          <div className="form-group" style={{ flex: '2', minWidth: '140px' }}>
            <label>Placeholder</label>
            <input
              type="text"
              value={field.placeholder ?? ''}
              onChange={(e) => update({ placeholder: e.target.value || undefined })}
            />
          </div>
        )}
        <div className="form-group" style={{ flex: '2', minWidth: '140px' }}>
          <label>Help text</label>
          <input
            type="text"
            value={field.help_text ?? ''}
            onChange={(e) => update({ help_text: e.target.value || undefined })}
          />
        </div>
        {field.type === 'number' ? (
          <>
            <div className="form-group" style={{ flex: '1', minWidth: '80px' }}>
              <label>Min</label>
              <input
                type="number"
                value={field.min ?? ''}
                onChange={(e) => update({ min: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
            <div className="form-group" style={{ flex: '1', minWidth: '80px' }}>
              <label>Max</label>
              <input
                type="number"
                value={field.max ?? ''}
                onChange={(e) => update({ max: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
          </>
        ) : isFile ? (
          <div className="form-group" style={{ flex: '1', minWidth: '110px' }}>
            <label>Max files</label>
            <input
              type="number"
              min={1}
              max={5}
              placeholder="5"
              value={field.max_files ?? ''}
              onChange={(e) =>
                update({
                  max_files: e.target.value
                    ? Math.min(5, Math.max(1, Math.round(Number(e.target.value))))
                    : undefined,
                })}
            />
          </div>
        ) : (
          <>
            <div className="form-group" style={{ flex: '1', minWidth: '80px' }}>
              <label>Max length</label>
              <input
                type="number"
                value={field.max_length ?? ''}
                onChange={(e) => update({ max_length: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
            <div className="form-group" style={{ flex: '1', minWidth: '80px' }}>
              <label>Min length</label>
              <input
                type="number"
                value={field.min_length ?? ''}
                onChange={(e) => update({ min_length: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
          </>
        )}
      </div>

      {isFile && (
        <div className="form-hint" style={{ marginBottom: '.75rem' }}>
          Accepts JPG, PNG, WebP, HEIC and PDF — up to 10 MB each, 20 MB per submission.
          Needs bot protection (reCAPTCHA or Turnstile). People get the files as download links.
        </div>
      )}

      {needsOptions && (
        <div className="form-group">
          <label>Options</label>
          {options.length === 0 && (
            <div className="form-hint" style={{ marginBottom: '.5rem' }}>No options yet.</div>
          )}
          {options.map((opt, idx) => (
            <div
              key={idx}
              style={{ display: 'flex', gap: '.5rem', marginBottom: '.5rem', alignItems: 'center' }}
            >
              <input
                type="text"
                value={opt.value}
                onChange={(e) => updateOption(idx, { value: e.target.value })}
                placeholder="value"
                style={{ flex: '1', minWidth: '120px' }}
              />
              <input
                type="text"
                value={opt.label}
                onChange={(e) => updateOption(idx, { label: e.target.value })}
                placeholder="Label"
                style={{ flex: '2', minWidth: '140px' }}
              />
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => removeOption(idx)}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={addOption}>
            + Add option
          </button>
        </div>
      )}

      <button type="button" className="btn btn-danger btn-sm" onClick={onRemove}>
        Remove field
      </button>
    </div>
  );
}
